import { ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import type { Dispatch } from '@ps/web-core-store';
import { Input, StarsButton, Translation } from '@ps/web-shared-components';
import { intervalToDuration } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ResendButton from '@swf/components/ResendButton/ResendButton';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';

import { CashierFlowType } from '../../../../types/Cashier';
import Alert from '../../../Alert/Alert';
import Container from '../../../Container/Container';

import {
  TXTCLI_Confirm,
  TXTCLI_Hi_N0X,
  TXTWCL_Email_verification,
  TXTWCL_Enter_code,
  TXTWCL_Help_with_deposits,
  TXTWCL_Help_with_withdrawals,
  TXTWCL_Just_so_you_know,
  TXTWCL_Please_enter_the_code_we_sent__ELL,
  TXTWCL_Please_enter_your_codeX,
  TXTWCL_Resend_code,
  TXTWCL_We_have_resent_your_codeX
} from './EmailCode.messages.json';
import {
  alertWrapper,
  boldEmail,
  buttonsWrapper,
  codeVerificationWrapper,
  error
} from './EmailCode.scss';
import HelpSectionContent from './components/HelpSectionContent/HelpSectionContent';
import { getFieldValidation } from './utils';

const flowMapping: Partial<
  Record<CashierFlowType, { title: TranslationTag; previousFlowStep: string }>
> = {
  [CashierFlowType.FirstWithdraw]: {
    title: TXTWCL_Help_with_withdrawals,
    previousFlowStep: '#withdraw.email-verification'
  },
  [CashierFlowType.FirstP2pTransfer]: {
    title: TXTWCL_Just_so_you_know,
    previousFlowStep: '#p-2-p-transfer.email-verification'
  },
  [CashierFlowType.FirstDeposit]: {
    title: TXTWCL_Help_with_deposits,
    previousFlowStep: '#deposit.email-verification'
  }
} as const;

const WithdrawEmailCode: React.FC = () => {
  const { localSettings } = useDispatch<Dispatch>();
  const emailVerificationDateTime = useSelector(
    select.localSettings.getCashier
  )?.emailVerificationDateTime;
  const {
    data: { cashierFlow }
  } = useWizardContext();

  const { seconds } = intervalToDuration({
    start: emailVerificationDateTime ? new Date(emailVerificationDateTime) : new Date(),
    end: new Date()
  });

  const firstName = useSelector(select.account.getFirstName);
  const emailAddress = useSelector(select.account.getEmail);
  const [codeResentInfo, setCodeResentInfo] = useState(false);
  const [countdown, setCountdown] = useState(seconds || seconds === 0 ? 30 - seconds : 0);
  const { jumpBack } = useWizardJump();
  const onBackClick = () => {
    const previousFlowStep = flowMapping[cashierFlow]?.previousFlowStep;
    if (previousFlowStep) {
      jumpBack(previousFlowStep);
    }
  };
  const {
    account: { sendValidationEmail }
  } = useDispatch<Dispatch>();

  const sendVerificationEmail = useCallback(() => {
    sendValidationEmail();
    localSettings.set({ cashier: { emailVerificationDateTime: new Date().toString() } });
    setCodeResentInfo(true);
    setCountdown(30);
  }, [sendValidationEmail, localSettings]);

  return (
    <Container
      page={WithdrawEmailCode}
      headerProps={{
        title: <Translation {...TXTWCL_Email_verification} />,
        buttons: { back: true, onBackClick }
      }}
      pageTopTitle={<Translation {...TXTCLI_Hi_N0X} values={{ 0: firstName }} />}
      pageTopSubtitle={
        <Translation
          {...TXTWCL_Please_enter_the_code_we_sent__ELL}
          values={{ 0: <span className={boldEmail}>{emailAddress}</span> }}
        />
      }
      helpSupportResolver={ResolverTag.HelpUpdateDetails}
      helpSection={{
        title: flowMapping[cashierFlow]?.title || TXTWCL_Help_with_withdrawals,
        body: <HelpSectionContent />
      }}
    >
      <div className={codeVerificationWrapper}>
        {codeResentInfo && (
          <div className={alertWrapper}>
            <Alert variant="success">
              <Translation {...TXTWCL_We_have_resent_your_codeX} />
            </Alert>
          </div>
        )}
        <Input
          id="emailVerificationCode"
          name="emailVerificationCode"
          type="tel"
          label={TXTWCL_Enter_code}
          showValidationTick={false}
          inputMode="numeric"
          className={error}
          validators={[...getFieldValidation(3, TXTWCL_Please_enter_your_codeX)]}
        />
        <div className={buttonsWrapper}>
          <StarsButton translation={TXTCLI_Confirm} type="submit" />
          {countdown <= 0 ? (
            <StarsButton
              translation={TXTWCL_Resend_code}
              variant="secondary"
              onClick={() => sendVerificationEmail()}
            />
          ) : (
            <ResendButton countdown={countdown} setCountdown={setCountdown} />
          )}
        </div>
      </div>
    </Container>
  );
};

WithdrawEmailCode.displayName = 'WithdrawEmailCode';

export default WithdrawEmailCode;
